import { IconButton } from "@mui/material";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import FavoriteStarIcon from "@entrilia/shared/components/FavoriteStarIcon.tsx";
import type { FavoriteReportsApiClient } from "../../../../../../api/biApi";
import biApi from "../../../../../../api/biApi";
import type { ReportUsage } from "../../../../../../api/types/svTypes";
import useFavoriteReports from "../../../report-lists/reports/hooks/useFavoriteReports";
import { selectCurrentReport, selectIsNewReport } from "../../store/selectors/currentReportSelectors";

const getFavoriteApi = (reportUsage: ReportUsage): FavoriteReportsApiClient | undefined => {
  switch (reportUsage) {
    case "Ui":
      return biApi.uiReports;
    case "Api":
      return biApi.apiReports;
    case "System":
      return biApi.systemReports;
    default:
      return undefined;
  }
};

const FavoriteReportStar = ({ readonlyMode }: { readonlyMode?: boolean }) => {
  const report = useSelector(selectCurrentReport);
  const isNewReport = useSelector(selectIsNewReport);

  const favoriteApi = useMemo(() => (report ? getFavoriteApi(report.reportUsage) : undefined), [report]);

  if (!report || isNewReport || !favoriteApi) {
    return null;
  }

  return (
    <FavoriteStarInner
      key={report.reportId}
      reportId={report.reportId}
      clientCode={report.clientCode}
      favoriteApi={favoriteApi}
      readonlyMode={readonlyMode}
    />
  );
};

interface FavoriteStarInnerProps {
  reportId: string;
  clientCode: string;
  favoriteApi: FavoriteReportsApiClient;
  readonlyMode?: boolean;
}

const FavoriteStarInner = ({ reportId, clientCode, favoriteApi, readonlyMode }: FavoriteStarInnerProps) => {
  const { favoriteIds, toggleFavorite } = useFavoriteReports({
    clientCode,
    getFavoriteReports: favoriteApi.getFavoriteReports,
    toggleFavoriteReport: favoriteApi.toggleFavoriteReport,
  });

  const isFavorite = favoriteIds.has(reportId);

  return (
    <IconButton onClick={() => toggleFavorite(reportId)} disabled={readonlyMode} disableRipple>
      <FavoriteStarIcon isFavorite={isFavorite} readonlyMode={readonlyMode} />
    </IconButton>
  );
};

export default FavoriteReportStar;
